import type { WidgetConfig, Portfolio } from '@/modules/ctx-dashboard';
import type { DashboardRepresentation } from '@/clients/dashboardapi/v2';
import Utils from '@/assets/js/utils/Utils';
import { Ref, ref, UnwrapRef, watch } from 'vue';

export type WizardTab<P = any> = {
    tabConfig: Ref<UnwrapRef<P>>,
    valid: Ref<boolean>,
    errors: Ref<Record<string, string[]>>,
    onShown: () => void,
    testValidity: () => boolean,
    validate: () => void,
}

type WizardTabProps<P = any> = {
    widgetConfig: WidgetConfig,
    widgetPortfolio: Portfolio,
    widgetDashboard: DashboardRepresentation,
    mode?: 'create' | 'edit',
    action?: string | null,
    params?: Partial<P> | ((widget: WidgetConfig) => Partial<P>)
}

type WizardTabEmits = {
    (e: 'update:valid', isValid: boolean): void;
    (e: 'update:widgetConfig', widgetConfig: WidgetConfig): void;
}

export function useWizardTab<P = any>(props: WizardTabProps<P>, emits: WizardTabEmits, defaultConfig?: P): WizardTab<P> {
    const tabConfig = ref<P>(defaultConfig || {} as P);
    const valid = ref(false);
    const errors = ref<Record<string, string[]>>({});

    const runtimeConfig = ref<(widget: WidgetConfig) => Partial<P>>(() => defaultConfig || {});

    function onParamChanged() {
        if (typeof props.params === 'function') {
            runtimeConfig.value = props.params;
        } else {
            runtimeConfig.value = () => props.params || {};
        }
        tabConfig.value = applyTabConfig() as any;
    }

    function applyTabConfig(): P {
        const runtimeTabConfig = runtimeConfig.value(Utils.deepCopy(props.widgetConfig));
        if (defaultConfig) {
            return Object.assign(Utils.deepCopy(defaultConfig), runtimeTabConfig);
        }
        return runtimeTabConfig as P;
    }

    function onShown() {
        onParamChanged();
        onChange();
    }

    function onChange() {
        tabConfig.value = applyTabConfig() as any;
        testValidity();
        emits('update:widgetConfig', props.widgetConfig);
    }

    function testValidity(): boolean {
        valid.value = Object.keys(errors.value).length === 0;
        emits('update:valid', valid.value);
        return valid.value;
    }

    function validate(): void {
        // currently we do not need to validate anything for all wizard tabs
        errors.value = {};
    }

    watch(() => props.widgetConfig, onChange, { deep: true });
    watch(() => props.params, onParamChanged, { deep: true });

    onParamChanged();

    return {
        tabConfig,
        valid,
        errors,
        onShown,
        testValidity,
        validate,
    };
}
